import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { StopRing } from '@/components/sat/journey/StopRing';
import { COURSE_HREF, MARKETING_CTAS } from '@/lib/marketingCtas';
import type { Audience } from './roleKit';

/**
 * CourseHook strip (spec 010 §4): role pages only, directly under the hero.
 * Eight empty dashed rings (the course's eight areas, nothing earned yet), one line, one outline link.
 * Static: no motion, no data, no requests.
 */
const COPY: Record<Audience, { line: string; cta: string; hint: string }> = {
  students: {
    line: 'Studying for the SAT? The course is free, no account.',
    cta: MARKETING_CTAS.courseHookStart.label,
    hint: ' the free SAT course',
  },
  parents: {
    line: 'Your student can start the free SAT course today. Link accounts to follow along.',
    cta: MARKETING_CTAS.courseHookStart.label,
    hint: ' the free SAT course',
  },
  tutors: {
    line: 'SAT students arrive with a diagnostic done.',
    cta: 'See the course →',
    hint: '',
  },
};

export function CourseHookStrip({ audience }: { audience: Audience }) {
  const { line, cta, hint } = COPY[audience];
  return (
    <section aria-label="Free SAT course" data-testid="course-hook-strip" className="border-y bg-muted/40">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-4 py-6 sm:px-6 lg:flex-row lg:items-center lg:gap-10">
        <div aria-hidden className="grid w-fit grid-cols-4 gap-x-3 gap-y-2 sm:grid-cols-8" data-testid="course-hook-rings">
          {Array.from({ length: 8 }, (_, i) => (
            <span key={i} className="block h-11 w-11">
              <span className="block origin-top-left scale-[.7]">
                <StopRing scorePct={null} band="NOT_STARTED" unearned fill="instant">
                  <span className="block h-1.5 w-1.5 rounded-full bg-muted-foreground/30" />
                </StopRing>
              </span>
            </span>
          ))}
        </div>
        <p className="flex-1 text-balance text-base font-medium leading-snug">{line}</p>
        <Button asChild variant="outline" className="w-full shrink-0 sm:w-auto">
          <Link to={COURSE_HREF}>
            {cta}
            {hint && <span className="sr-only">{hint}</span>}
          </Link>
        </Button>
      </div>
    </section>
  );
}
